import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, MapPin, Pencil, Trash2, Users, Store, Target, Percent, UserCheck, Scissors, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminZones() {
  const { getAuthHeaders } = useAuth();
  const [zones, setZones] = useState([]);
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [goalsZone, setGoalsZone] = useState(null); // zone whose team goals we're editing
  const [zoneBarbers, setZoneBarbers] = useState([]);
  const [barberGoalsDraft, setBarberGoalsDraft] = useState({}); // { barber_id: { avg_ticket_goal, services_goal, products_revenue_goal } }
  const [savingGoals, setSavingGoals] = useState(false);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    manager_id: '',
    branch_ids: [],
    monthly_sales_target: '',
    commission_percentage: '',
    weekly_overrides: ['', '', '', '', ''],
    branch_targets: {}, // { branch_id: { services: str, products: str } }
  });

  useEffect(() => {
    fetchZones();
    fetchBranches();
    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${API}/zones`, getAuthHeaders());
      setZones(res.data);
    } catch (error) {
      toast.error('Error al cargar zonas');
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, getAuthHeaders());
      setBranches(res.data);
    } catch (error) { /* handled */ }
  };

  const fetchManagers = async () => {
    try {
      const res = await axios.get(`${API}/zones/managers/available`, getAuthHeaders());
      setManagers(res.data);
    } catch (error) { /* handled */ }
  };

  const resetForm = () => {
    setFormData({ name: '', manager_id: '', branch_ids: [], monthly_sales_target: '', commission_percentage: '', weekly_overrides: ['', '', '', '', ''], branch_targets: {} });
    setEditingZone(null);
  };

  const handleEdit = (zone) => {
    setEditingZone(zone);
    const overrides = zone.weekly_overrides || [];
    const targets = {};
    Object.entries(zone.branch_targets || {}).forEach(([bid, vals]) => {
      targets[bid] = {
        services: vals?.services ? String(vals.services) : '',
        products: vals?.products ? String(vals.products) : '',
      };
    });
    setFormData({
      name: zone.name,
      manager_id: zone.manager_id || '',
      branch_ids: zone.branch_ids || [],
      monthly_sales_target: zone.monthly_sales_target?.toString() || '0',
      commission_percentage: zone.commission_percentage?.toString() || '0',
      weekly_overrides: [0, 1, 2, 3, 4].map(i => (overrides[i] && overrides[i] > 0 ? overrides[i].toString() : '')),
      branch_targets: targets,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre de la zona es requerido');
      return;
    }

    const overridesPayload = formData.weekly_overrides.map(v => {
      const num = parseFloat(v);
      return isNaN(num) || num <= 0 ? 0 : num;
    });

    // Build branch_targets payload: only include selected branches with non-zero values
    const branchTargetsPayload = {};
    formData.branch_ids.forEach(bid => {
      const t = formData.branch_targets[bid] || {};
      const services = parseFloat(t.services) || 0;
      const products = parseFloat(t.products) || 0;
      if (services > 0 || products > 0) {
        branchTargetsPayload[bid] = { services, products };
      }
    });

    const payload = {
      name: formData.name.trim(),
      manager_id: formData.manager_id || null,
      branch_ids: formData.branch_ids,
      monthly_sales_target: parseFloat(formData.monthly_sales_target) || 0,
      commission_percentage: parseFloat(formData.commission_percentage) || 0,
      weekly_overrides: overridesPayload,
      branch_targets: branchTargetsPayload,
    };

    try {
      if (editingZone) {
        await axios.put(`${API}/zones/${editingZone.id}`, payload, getAuthHeaders());
        toast.success('Zona actualizada');
      } else {
        await axios.post(`${API}/zones`, payload, getAuthHeaders());
        toast.success('Zona creada');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchZones();
      fetchManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar zona');
    }
  };

  const handleDelete = async (zoneId) => {
    if (!window.confirm('¿Eliminar esta zona?')) return;
    try {
      await axios.delete(`${API}/zones/${zoneId}`, getAuthHeaders());
      toast.success('Zona eliminada');
      fetchZones();
      fetchManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openGoalsDialog = async (zone) => {
    setGoalsZone(zone);
    setLoadingGoals(true);
    setZoneBarbers([]);
    setBarberGoalsDraft({});
    try {
      // Fetch barbers per branch in parallel (existing endpoint supports ?branch_id=)
      const branchIds = zone.branch_ids || [];
      const results = await Promise.all(
        branchIds.map(bid =>
          axios.get(`${API}/barbers?branch_id=${bid}`, getAuthHeaders()).then(r => r.data).catch(() => [])
        )
      );
      const flat = results.flat().filter(b => b.role_type !== 'receptionist');
      // De-dupe (barbers won't repeat, but just in case)
      const seen = new Set();
      const unique = flat.filter(b => {
        if (seen.has(b.id)) return false;
        seen.add(b.id);
        return true;
      });
      setZoneBarbers(unique);
      const draft = {};
      unique.forEach(b => {
        draft[b.id] = {
          avg_ticket_goal: b.avg_ticket_goal ? String(b.avg_ticket_goal) : '',
          services_goal: b.services_goal ? String(b.services_goal) : '',
          products_revenue_goal: b.products_revenue_goal ? String(b.products_revenue_goal) : '',
          extra_services_goal: b.extra_services_goal ? String(b.extra_services_goal) : '',
          neurone_revenue_goal: b.neurone_revenue_goal ? String(b.neurone_revenue_goal) : '',
        };
      });
      setBarberGoalsDraft(draft);
    } catch (e) {
      toast.error('Error al cargar barberos de la zona');
    } finally {
      setLoadingGoals(false);
    }
  };

  const closeGoalsDialog = () => {
    setGoalsZone(null);
    setZoneBarbers([]);
    setBarberGoalsDraft({});
  };

  const saveTeamGoals = async () => {
    if (!goalsZone) return;
    setSavingGoals(true);
    try {
      // Only save barbers whose goals changed from the initial values
      const updates = zoneBarbers.map(b => {
        const draft = barberGoalsDraft[b.id] || {};
        const nextAvg = parseFloat(draft.avg_ticket_goal) || 0;
        const nextSvc = parseInt(draft.services_goal) || 0;
        const nextPrd = parseFloat(draft.products_revenue_goal) || 0;
        const nextExtra = parseInt(draft.extra_services_goal) || 0;
        const nextNeurone = parseFloat(draft.neurone_revenue_goal) || 0;
        const curAvg = b.avg_ticket_goal || 0;
        const curSvc = b.services_goal || 0;
        const curPrd = b.products_revenue_goal || 0;
        const curExtra = b.extra_services_goal || 0;
        const curNeurone = b.neurone_revenue_goal || 0;
        const changed = nextAvg !== curAvg || nextSvc !== curSvc || nextPrd !== curPrd || nextExtra !== curExtra || nextNeurone !== curNeurone;
        return changed ? {
          id: b.id,
          avg_ticket_goal: nextAvg,
          services_goal: nextSvc,
          products_revenue_goal: nextPrd,
          extra_services_goal: nextExtra,
          neurone_revenue_goal: nextNeurone,
        } : null;
      }).filter(Boolean);

      if (updates.length === 0) {
        toast.info('Sin cambios que guardar');
        setSavingGoals(false);
        return;
      }

      await Promise.all(updates.map(u =>
        axios.put(`${API}/barbers/${u.id}/goals`, {
          avg_ticket_goal: u.avg_ticket_goal,
          services_goal: u.services_goal,
          products_revenue_goal: u.products_revenue_goal,
          extra_services_goal: u.extra_services_goal,
          neurone_revenue_goal: u.neurone_revenue_goal,
        }, getAuthHeaders())
      ));
      toast.success(`Metas actualizadas para ${updates.length} colaborador${updates.length === 1 ? '' : 'es'}`);
      closeGoalsDialog();
    } catch (e) {
      toast.error('Error al guardar metas');
    } finally {
      setSavingGoals(false);
    }
  };

  const toggleBranch = (branchId) => {
    setFormData(prev => ({
      ...prev,
      branch_ids: prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter(id => id !== branchId)
        : [...prev.branch_ids, branchId]
    }));
  };

  // Branches already assigned to other zones
  const assignedBranchIds = zones
    .filter(z => !editingZone || z.id !== editingZone.id)
    .flatMap(z => z.branch_ids || []);

  return (
    <AdminLayout title="Zonas Gerenciales">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg text-zinc-300">Administra zonas, gerentes y sucursales asignadas</h2>
          </div>
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="bg-gold hover:bg-gold/90 text-black"
            data-testid="create-zone-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Zona
          </Button>
        </div>

        {/* Zone Cards */}
        {zones.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 text-lg">No hay zonas configuradas</p>
              <p className="text-zinc-600 text-sm mt-1">Crea una zona y asigna un gerente y sucursales</p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {zones.map((zone) => (
              <Card key={zone.id} className="bg-zinc-900/50 border-zinc-800 p-5" data-testid={`zone-card-${zone.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gold/20 rounded-sm flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">{zone.name}</h3>
                      <p className="text-sm text-zinc-500">
                        {zone.branches?.length || 0} sucursales
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(zone)} className="text-zinc-400 hover:text-white h-8 w-8">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(zone.id)} className="text-zinc-400 hover:text-red-500 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Manager */}
                <div className="flex items-center gap-2 mb-3 p-2 bg-zinc-800/50 rounded-sm">
                  <UserCheck className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-zinc-300">
                    {zone.manager_name || <span className="text-zinc-600 italic">Sin gerente asignado</span>}
                  </span>
                </div>

                {/* Branches */}
                <div className="space-y-1 mb-3">
                  {zone.branches?.map(b => (
                    <div key={b.id} className="flex items-center gap-2 px-2 py-1">
                      <Store className="w-3 h-3 text-zinc-500" />
                      <span className="text-sm text-zinc-400">{b.name}</span>
                    </div>
                  ))}
                </div>

                {/* Targets */}
                <div className="flex gap-3 mt-3 pt-3 border-t border-zinc-800 items-center justify-between">
                  <div className="flex gap-3 flex-wrap">
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Target className="w-3 h-3" />
                      Meta: <span className="text-zinc-300">${zone.monthly_sales_target?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Percent className="w-3 h-3" />
                      Comisión: <span className="text-zinc-300">{zone.commission_percentage}%</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openGoalsDialog(zone)}
                    className="h-7 text-xs border-zinc-700 text-zinc-300 hover:bg-gold/10 hover:text-gold hover:border-gold/40"
                    data-testid={`team-goals-btn-${zone.id}`}
                    disabled={!zone.branch_ids || zone.branch_ids.length === 0}
                  >
                    <Users className="w-3 h-3 mr-1.5" />
                    Metas Equipo
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-white text-xl">
                {editingZone ? 'Editar Zona' : 'Nueva Zona'}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-4 py-2">
                {/* Name */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre de la Zona</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Zona Norte"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="zone-name-input"
                  />
                </div>

                {/* Manager */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Gerente de Zona</Label>
                  <Select
                    value={formData.manager_id || "none"}
                    onValueChange={(val) => setFormData({ ...formData, manager_id: val === "none" ? "" : val })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="zone-manager-select">
                      <SelectValue placeholder="Seleccionar gerente" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="none" className="text-zinc-400">Sin asignar</SelectItem>
                      {managers.map(m => (
                        <SelectItem key={m.id} value={m.id} className="text-white">
                          {m.name} ({m.username})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-600">
                    Solo usuarios con rol "Encargado" aparecen aquí. Crea el usuario primero en Encargados.
                  </p>
                </div>

                {/* Branch assignment */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Sucursales Asignadas</Label>
                  <div className="space-y-2 bg-zinc-800/50 rounded-sm p-3 border border-zinc-700">
                    {branches.map(branch => {
                      const isAssignedElsewhere = assignedBranchIds.includes(branch.id);
                      const isSelected = formData.branch_ids.includes(branch.id);
                      return (
                        <label
                          key={branch.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            isAssignedElsewhere && !isSelected
                              ? 'opacity-40 cursor-not-allowed'
                              : isSelected ? 'bg-gold/10 border border-gold/30' : 'hover:bg-zinc-700/50 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isAssignedElsewhere && !isSelected}
                            onChange={() => toggleBranch(branch.id)}
                            className="rounded bg-zinc-700 border-zinc-600 text-gold"
                            data-testid={`zone-branch-checkbox-${branch.id}`}
                          />
                          <Store className="w-4 h-4 text-zinc-500" />
                          <span className={`text-sm ${isSelected ? 'text-white' : 'text-zinc-400'}`}>
                            {branch.name}
                          </span>
                          {isAssignedElsewhere && !isSelected && (
                            <span className="text-xs text-zinc-600 ml-auto">Asignada a otra zona</span>
                          )}
                        </label>
                      );
                    })}
                    {branches.length === 0 && (
                      <p className="text-zinc-600 text-sm text-center py-2">No hay sucursales registradas</p>
                    )}
                  </div>
                </div>

                {/* Targets */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Meta Mensual Zona ($)</Label>
                    <Input
                      type="number"
                      value={formData.monthly_sales_target}
                      onChange={(e) => setFormData({ ...formData, monthly_sales_target: e.target.value })}
                      placeholder="0 (auto = suma sucursales)"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="zone-target-input"
                    />
                    <p className="text-[10px] text-zinc-600">Deja en 0 para usar la suma automática de las metas por sucursal.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Comisión Gerente (%)</Label>
                    <Input
                      type="number"
                      value={formData.commission_percentage}
                      onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                      placeholder="0"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="zone-commission-input"
                    />
                  </div>
                </div>

                {/* Per-branch targets */}
                {formData.branch_ids.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="branch-targets-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-400 flex items-center gap-1.5">
                        <Target className="w-3 h-3 text-gold" />
                        Metas por Sucursal
                      </Label>
                      {(() => {
                        const totalSvc = formData.branch_ids.reduce((s, bid) => s + (parseFloat(formData.branch_targets[bid]?.services) || 0), 0);
                        const totalPrd = formData.branch_ids.reduce((s, bid) => s + (parseFloat(formData.branch_targets[bid]?.products) || 0), 0);
                        return (
                          <span className="text-[10px] text-zinc-500">
                            Suma: ${(totalSvc + totalPrd).toLocaleString('es-MX')} (Serv ${totalSvc.toLocaleString('es-MX')} + Prod ${totalPrd.toLocaleString('es-MX')})
                          </span>
                        );
                      })()}
                    </div>
                    <p className="text-[10px] text-zinc-600">
                      Define la meta mensual de Servicios y Productos para cada sucursal. Se muestran progresos independientes en el dashboard del Gerente.
                    </p>
                    <div className="space-y-2">
                      {formData.branch_ids.map(bid => {
                        const branch = branches.find(b => b.id === bid);
                        if (!branch) return null;
                        const t = formData.branch_targets[bid] || {};
                        return (
                          <div key={bid} className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-700" data-testid={`branch-target-row-${bid}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Store className="w-3.5 h-3.5 text-zinc-500" />
                              <span className="text-sm text-white font-medium">{branch.name}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[10px] text-zinc-500">Meta Servicios ($)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="100"
                                  value={t.services || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    branch_targets: {
                                      ...formData.branch_targets,
                                      [bid]: { ...t, services: e.target.value }
                                    }
                                  })}
                                  placeholder="0"
                                  className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                                  data-testid={`branch-target-services-${bid}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-zinc-500">Meta Productos ($)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="100"
                                  value={t.products || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    branch_targets: {
                                      ...formData.branch_targets,
                                      [bid]: { ...t, products: e.target.value }
                                    }
                                  })}
                                  placeholder="0"
                                  className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                                  data-testid={`branch-target-products-${bid}`}
                                />
                              </div>
                            </div>
                            {(parseFloat(t.services) > 0 || parseFloat(t.products) > 0) && (
                              <p className="text-[10px] text-gold mt-1.5">
                                Total sucursal: ${((parseFloat(t.services) || 0) + (parseFloat(t.products) || 0)).toLocaleString('es-MX')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Weekly overrides */}
                {parseFloat(formData.monthly_sales_target) > 0 && (
                  <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="weekly-overrides-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-400">Metas Semanales (opcional)</Label>
                      <span className="text-[10px] text-zinc-500">
                        Auto ≈ ${Math.round(parseFloat(formData.monthly_sales_target) / 5).toLocaleString()}/sem
                      </span>
                    </div>
                    <p className="text-[10px] text-zinc-600">
                      Si dejas un campo vacío se usará el reparto automático ({Math.round(parseFloat(formData.monthly_sales_target) / 5).toLocaleString()} aprox. por semana).
                    </p>
                    <div className="grid grid-cols-5 gap-2">
                      {[0, 1, 2, 3, 4].map(i => (
                        <div key={i} className="space-y-1">
                          <Label className="text-[10px] text-zinc-500">Sem. {i + 1}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="100"
                            value={formData.weekly_overrides[i]}
                            onChange={(e) => {
                              const next = [...formData.weekly_overrides];
                              next[i] = e.target.value;
                              setFormData({ ...formData, weekly_overrides: next });
                            }}
                            placeholder="auto"
                            className="bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                            data-testid={`weekly-override-input-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSubmit}
                  className="w-full bg-gold hover:bg-gold/90 text-black mt-2"
                  data-testid="zone-save-btn"
                >
                  {editingZone ? 'Actualizar Zona' : 'Crear Zona'}
                </Button>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Team Goals Dialog */}
        <Dialog open={!!goalsZone} onOpenChange={(o) => !o && closeGoalsDialog()}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh]" data-testid="team-goals-dialog">
            <DialogHeader>
              <DialogTitle className="text-white text-xl flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" />
                Metas del Equipo — {goalsZone?.name}
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">
                Define las metas personales mensuales de barberos y estilistas de las sucursales de esta zona.
              </p>
            </DialogHeader>

            {loadingGoals ? (
              <div className="text-center text-zinc-500 py-12">Cargando colaboradores...</div>
            ) : zoneBarbers.length === 0 ? (
              <div className="text-center text-zinc-500 py-12">
                <Users className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                <p>No hay barberos o estilistas activos en las sucursales de esta zona.</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[60vh] pr-4">
                <div className="space-y-4 py-2">
                  {/* Group by branch */}
                  {(goalsZone?.branch_ids || []).map(bid => {
                    const branch = branches.find(b => b.id === bid);
                    const barbersInBranch = zoneBarbers.filter(x => x.branch_id === bid);
                    if (barbersInBranch.length === 0) return null;
                    return (
                      <div key={bid} className="space-y-2" data-testid={`goals-branch-group-${bid}`}>
                        <div className="flex items-center gap-2 pb-1 border-b border-zinc-800">
                          <Store className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-sm text-white font-semibold">{branch?.name || 'Sucursal'}</span>
                          <span className="text-[10px] text-zinc-500">({barbersInBranch.length} colaborador{barbersInBranch.length === 1 ? '' : 'es'})</span>
                        </div>
                        <div className="space-y-2">
                          {barbersInBranch.map(b => {
                            const draft = barberGoalsDraft[b.id] || {};
                            return (
                              <div key={b.id} className="bg-zinc-800/40 border border-zinc-700 rounded-sm p-3" data-testid={`goals-row-${b.id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[11px] text-zinc-300">
                                    {b.name?.charAt(0).toUpperCase() || '?'}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm text-white font-medium">{b.name}</p>
                                    <p className="text-[10px] text-zinc-500">
                                      {b.role_type === 'stylist' ? 'Estilista' : 'Barbero'}
                                    </p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <Label className="text-[10px] text-zinc-500 flex items-center gap-1">
                                      <Target className="w-2.5 h-2.5 text-purple-400" /> Ticket Prom. ($)
                                    </Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="10"
                                      value={draft.avg_ticket_goal || ''}
                                      onChange={(e) => setBarberGoalsDraft({
                                        ...barberGoalsDraft,
                                        [b.id]: { ...draft, avg_ticket_goal: e.target.value }
                                      })}
                                      placeholder="0"
                                      className="bg-zinc-900 border-zinc-700 text-white text-xs h-8"
                                      data-testid={`goal-avg-ticket-${b.id}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[10px] text-zinc-500 flex items-center gap-1">
                                      <Scissors className="w-2.5 h-2.5 text-green-400" /> Servicios (#)
                                    </Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={draft.services_goal || ''}
                                      onChange={(e) => setBarberGoalsDraft({
                                        ...barberGoalsDraft,
                                        [b.id]: { ...draft, services_goal: e.target.value }
                                      })}
                                      placeholder="0"
                                      className="bg-zinc-900 border-zinc-700 text-white text-xs h-8"
                                      data-testid={`goal-services-${b.id}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[10px] text-zinc-500 flex items-center gap-1">
                                      <Package className="w-2.5 h-2.5 text-blue-400" /> Productos ($)
                                    </Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="100"
                                      value={draft.products_revenue_goal || ''}
                                      onChange={(e) => setBarberGoalsDraft({
                                        ...barberGoalsDraft,
                                        [b.id]: { ...draft, products_revenue_goal: e.target.value }
                                      })}
                                      placeholder="0"
                                      className="bg-zinc-900 border-zinc-700 text-white text-xs h-8"
                                      data-testid={`goal-products-${b.id}`}
                                    />
                                  </div>
                                </div>
                                {/* EXTRA + NEURONE row */}
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <div>
                                    <Label className="text-[10px] text-orange-400 flex items-center gap-1">
                                      EXTRA (#)
                                      <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400 uppercase">Extra</span>
                                    </Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={draft.extra_services_goal || ''}
                                      onChange={(e) => setBarberGoalsDraft({
                                        ...barberGoalsDraft,
                                        [b.id]: { ...draft, extra_services_goal: e.target.value }
                                      })}
                                      placeholder="0"
                                      className="bg-zinc-900 border-zinc-700 text-white text-xs h-8"
                                      data-testid={`goal-extra-${b.id}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[10px] text-purple-400 flex items-center gap-1">
                                      NEURONE ($)
                                      <span className="text-[8px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400 uppercase">Neurone</span>
                                    </Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="100"
                                      value={draft.neurone_revenue_goal || ''}
                                      onChange={(e) => setBarberGoalsDraft({
                                        ...barberGoalsDraft,
                                        [b.id]: { ...draft, neurone_revenue_goal: e.target.value }
                                      })}
                                      placeholder="0"
                                      className="bg-zinc-900 border-zinc-700 text-white text-xs h-8"
                                      data-testid={`goal-neurone-${b.id}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            <div className="flex gap-2 pt-3 border-t border-zinc-800">
              <Button variant="ghost" onClick={closeGoalsDialog} className="flex-1 text-zinc-400" data-testid="cancel-team-goals-btn">
                Cancelar
              </Button>
              <Button
                onClick={saveTeamGoals}
                disabled={savingGoals || zoneBarbers.length === 0}
                className="flex-1 bg-gold hover:bg-gold/90 text-black"
                data-testid="save-team-goals-btn"
              >
                {savingGoals ? 'Guardando...' : 'Guardar metas'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
